import { useCallback, useEffect, useState } from 'react';
import {
  FORM_FIELD_GROUPS,
  deleteFormFieldOption,
  fetchFormFieldOptions,
  upsertFormFieldOption,
  type FormFieldOption,
} from '../../../lib/applicationFormSettings';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import { AfSettingsSection } from './AfSettingsSection';

export function ApplicationFormOptionsPanel() {
  const [rows, setRows] = useState<FormFieldOption[]>([]);
  const [fieldKey, setFieldKey] = useState(FORM_FIELD_GROUPS[0]?.key ?? 'apply_for');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [draft, setDraft] = useState({ value: '', label: '', sort_order: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchFormFieldOptions('account-creation'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dropdown options.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['form_field_options'],
    onChange: () => {
      void load();
    },
  });

  const visible = rows.filter((row) => row.field_key === fieldKey);
  const activeField = FORM_FIELD_GROUPS.find((group) => group.key === fieldKey);

  async function addOption(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.value.trim() || !draft.label.trim()) {
      setError('Value and label are required.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertFormFieldOption({
        field_key: fieldKey,
        value: draft.value.trim(),
        label: draft.label.trim(),
        sort_order: draft.sort_order || visible.length + 1,
        active: true,
      });
      setDraft({ value: '', label: '', sort_order: 0 });
      await load();
      setMessage('Option added.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add failed.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: FormFieldOption) {
    setSaving(true);
    setError(null);
    try {
      await upsertFormFieldOption({
        id: row.id,
        field_key: row.field_key,
        value: row.value,
        label: row.label,
        sort_order: row.sort_order,
        active: !row.active,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setSaving(false);
    }
  }

  async function removeOption(row: FormFieldOption) {
    if (!window.confirm(`Delete option "${row.label}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteFormFieldOption(row.id);
      await load();
      setMessage('Option removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="af-settings-loading">Loading dropdown options…</p>;

  return (
    <AfSettingsSection
      title="Form dropdown options"
      description="Options appear on the public form and Finance approval screen. Disabled options stay on old submissions but are hidden from new entries. Salesperson codes are managed under User management → Salesperson."
      badge={`${visible.length} options`}
      error={error}
      message={message}
    >
      <label className="af-settings-field af-settings-field--selector">
        <span>Field group</span>
        <select value={fieldKey} onChange={(e) => setFieldKey(e.target.value)}>
          {FORM_FIELD_GROUPS.map((group) => (
            <option key={group.key} value={group.key}>
              {group.label}
            </option>
          ))}
        </select>
        {activeField ? <span className="af-settings-field__hint">Editing: {activeField.label}</span> : null}
      </label>

      <form className="af-settings-add-card af-settings-add-card--top" onSubmit={(e) => void addOption(e)}>
        <h3 className="af-settings-add-card__title">Add option</h3>
        <div className="af-settings-form__grid af-settings-form__grid--3">
          <label className="af-settings-field">
            <span>Value (stored)</span>
            <input value={draft.value} onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))} />
          </label>
          <label className="af-settings-field">
            <span>Label (shown)</span>
            <input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} />
          </label>
          <label className="af-settings-field">
            <span>Sort order</span>
            <input
              type="number"
              value={draft.sort_order || ''}
              onChange={(e) => setDraft((d) => ({ ...d, sort_order: Number(e.target.value) || 0 }))}
            />
          </label>
        </div>
        <button type="submit" className="btn-primary btn-sm" disabled={saving}>
          Add option
        </button>
      </form>

      <div className="af-settings-table-wrap">
        <table className="af-settings-table">
          <thead>
            <tr>
              <th>Value</th>
              <th>Label</th>
              <th>Order</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="af-settings-table__empty">
                  No options for this field.
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr key={row.id} className={row.active ? '' : 'af-settings-table__row--muted'}>
                  <td>
                    <code className="af-settings-code">{row.value}</code>
                  </td>
                  <td>{row.label}</td>
                  <td>{row.sort_order}</td>
                  <td>
                    <span className={`af-settings-status${row.active ? ' af-settings-status--on' : ''}`}>
                      {row.active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="af-settings-table__actions">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={saving}
                      onClick={() => void toggleActive(row)}
                    >
                      {row.active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      disabled={saving}
                      onClick={() => void removeOption(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AfSettingsSection>
  );
}
